import { getLang } from './lang'

/** Rounding happens here and nowhere else (§12). */

const eur = new Intl.NumberFormat('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const eur0 = new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 })
const num0 = new Intl.NumberFormat('de-AT', { maximumFractionDigits: 0 })

export function money(v: number | null | undefined, currency = 'EUR'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  return `${eur.format(v)} ${currency === 'EUR' ? '€' : currency}`
}

export function money0(v: number | null | undefined, currency = 'EUR'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  return `${eur0.format(v)} ${currency === 'EUR' ? '€' : currency}`
}

/** 1 309 812 → "1,31 M €", 812 345 → "812 k €" — for stat strips where the full figure does not fit */
export function moneyCompact(v: number | null | undefined, currency = 'EUR'): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  const sym = currency === 'EUR' ? '€' : currency
  const abs = Math.abs(v)
  const sign = v < 0 ? '−' : ''
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2).replace('.', ',')} M ${sym}`
  if (abs >= 10_000) return `${sign}${Math.round(abs / 1000).toLocaleString('de-AT')} k ${sym}`
  return money0(v, currency)
}

export function amount(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  return eur.format(v)
}

export function signedAmount(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  const s = eur.format(Math.abs(v))
  return v < 0 ? `−${s}` : v > 0 ? `+${s}` : s
}

export function pct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  return `${(v * 100).toFixed(digits).replace('.', ',')} %`
}

export function signedPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  const s = `${Math.abs(v * 100).toFixed(digits).replace('.', ',')} %`
  return v < 0 ? `−${s}` : v > 0 ? `+${s}` : s
}

export function units(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  return num0.format(v)
}

export function basisLabel(basis: string): string {
  if (getLang() === 'de') return basis === 'delivered' ? 'frei Haus' : 'ab Werk'
  return basis === 'delivered' ? 'delivered' : 'ex works'
}

export function monthLabel(m: string): string {
  const [y, mm] = m.split('-')
  const names =
    getLang() === 'de'
      ? ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${names[Number(mm) - 1] ?? mm} ${y?.slice(2)}`
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '–'
  const d = new Date(iso)
  const loc = getLang() === 'de' ? 'de-AT' : 'en-GB'
  const date = d.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: '2-digit' })
  const time = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })
  return `${date}, ${time}`
}

export function age(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const h = Math.floor(ms / 3_600_000)
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))} min`
  if (h < 48) return `${h} h`
  return `${Math.floor(h / 24)} ${getLang() === 'de' ? 'T' : 'd'}`
}

/** relative effect for display: log coefficient → percent string */
export function logPct(value: number, digits = 2): string {
  const p = (Math.exp(value) - 1) * 100
  const s = `${Math.abs(p).toFixed(digits).replace('.', ',')} %`
  return p < 0 ? `−${s}` : p > 0 ? `+${s}` : '0 %'
}

/** A price performance index, 100 = at target. */
export function ppiLabel(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–'
  return v.toLocaleString(getLang() === 'de' ? 'de-AT' : 'en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}
